import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { CustomAuthProvider } from './contexts/CustomAuthContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { GameDataProvider } from './contexts/GameDataContext';
import ErrorBoundary from './components/Helper/ErrorBoundary';
import ProtectedRoute from './components/Helper/ProtectedRoute';
import FeatureGatedRoute from './components/Helper/FeatureGatedRoute';
import Layout from './components/MainPage/Layout';
import CookieBanner from './components/MainPage/CookieBanner';
import MapImagePreloader from './components/Map/MapImagePreloader';
import { AI_UI_ENABLED } from './config/env';

// Route pages are lazy-loaded so the initial bundle doesn't ship every page
// (several of these, e.g. the Map/Pokedex, are 800-1800 lines each) up front.
//
// After a redeploy, an already-open tab holds the old index referencing chunk
// filenames that no longer exist → "Failed to fetch dynamically imported
// module". retryLazy recovers by doing a single full reload (throttled per
// session) to pick up the new index, instead of surfacing a broken page.
function retryLazy<T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const last = Number(sessionStorage.getItem('chunkReloadAt') || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem('chunkReloadAt', String(Date.now()));
        window.location.reload();
        return await new Promise<never>(() => {}); // hang until the reload happens
      }
      throw err;
    }
  });
}

const Login = retryLazy(() => import('./components/ZitadelAuth/Login'));
const AuthCallback = retryLazy(() => import('./components/ZitadelAuth/AuthCallback'));
const Register = retryLazy(() => import('./components/ZitadelAuth/Register'));
const ForgotPassword = retryLazy(() => import('./components/Administration/ForgotPassword'));
const ResetPassword = retryLazy(() => import('./components/ZitadelAuth/ResetPassword'));
const VerifyEmail = retryLazy(() => import('./components/ZitadelAuth/VerifyEmail'));
const TOTPSetup = retryLazy(() => import('./components/ZitadelAuth/TOTPSetup'));
const HomePage = retryLazy(() => import('./components/MainPage/HomePage'));
const AdminOverview = retryLazy(() => import('./components/Administration/AdminOverview'));
const AdminUsers = retryLazy(() => import('./components/Administration/AdminUsers'));
const GameDataManagement = retryLazy(() => import('./components/Administration/GameDataManagement'));
const Profile = retryLazy(() => import('./components/MainPage/Profile'));
const Impressum = retryLazy(() => import('./components/MainPage/Impressum'));
const FAQ = retryLazy(() => import('./components/MainPage/FAQ'));
const DiscordDashboard = retryLazy(() => import('./components/Discord/DiscordDashboard'));
const DiscordServerSettings = retryLazy(() => import('./components/Discord/DiscordServerSettings'));
const InteractiveMap = retryLazy(() => import('./components/Map/InteractiveMap'));
const DamageCalculator = retryLazy(() => import('./components/DamageCalc/DamageCalculator'));
const Pokedex = retryLazy(() => import('./components/Pokedex/Pokedex'));
const PokemonPage = retryLazy(() => import('./components/Pokedex/PokemonPage'));
const DatabaseHub = retryLazy(() => import('./components/Compendium/DatabaseHub'));
const Compendium = retryLazy(() => import('./components/Compendium/Compendium'));
const MoveDetail = retryLazy(() => import('./components/Compendium/MoveDetail'));
const AbilityDetail = retryLazy(() => import('./components/Compendium/AbilityDetail'));
const ChatBubble = retryLazy(() => import('./components/Chat/ChatBubble'));
const TypeChart = retryLazy(() => import('./components/TypeChart/TypeChart'));
const CounterPage = retryLazy(() => import('./components/Counter/CounterPage'));
const SetsPage = retryLazy(() => import('./components/Sets/SetsPage'));
const DexGuesser = retryLazy(() => import('./components/Games/DexGuesser'));
const SpinWheel = retryLazy(() => import('./components/Games/SpinWheel'));
const RandomTeam = retryLazy(() => import('./components/Games/RandomTeam'));
const TypeQuiz = retryLazy(() => import('./components/Games/TypeQuiz'));
const GamesHub = retryLazy(() => import('./components/Games/GamesHub'));
const TeamBuilder = retryLazy(() => import('./components/TeamBuilder/TeamBuilder'));
const PvpHub = retryLazy(() => import('./components/Pvp/PvpHub'));
const DungeonsPage = retryLazy(() => import('./components/Dungeons/DungeonsPage'));
const DungeonView = retryLazy(() => import('./components/Dungeons/DungeonView'));
const BattleView = retryLazy(() => import('./components/Pvp/BattleView'));
const ReplayView = retryLazy(() => import('./components/Pvp/ReplayView'));
const SpectatorView = retryLazy(() => import('./components/Pvp/SpectatorView'));
const LoggingPanel = retryLazy(() => import('./components/Administration/LoggingPanel'));
const FeatureFlagsAdmin = retryLazy(() => import('./components/Administration/FeatureFlagsAdmin'));
const PvpStats = retryLazy(() => import('./components/Administration/PvpStats'));
const AtlasAdmin = retryLazy(() => import('./components/Administration/AtlasAdmin'));
const LearnsetGenerator = retryLazy(() => import('./components/Administration/LearnsetGenerator'));

import './App.css';

function App() {
  return (
    <ThemeProvider>
    <ErrorBoundary>
    <ToastProvider>
    <CustomAuthProvider>
      <FeatureFlagsProvider>
        <GameDataProvider>
        <Router>
          <div className="App">
            <CookieBanner />
            <MapImagePreloader />
            <Suspense fallback={null}>
            <Routes>
              {/* ── Home / Landing page ── */}
              <Route
                path="/"
                element={
                  <Layout>
                    <HomePage />
                  </Layout>
                }
              />

              {/* ── FAQ / Info ── */}
              <Route
                path="/faq"
                element={
                  <Layout>
                    <FAQ />
                  </Layout>
                }
              />

              {/* ── Map ── */}
              <Route
                path="/map"
                element={
                  <FeatureGatedRoute featureKey="feature_page_map">
                    <Layout showFooter={false} collapsibleHeader={true}>
                      <InteractiveMap />
                    </Layout>
                  </FeatureGatedRoute>
                }
              />

              {/* ── Damage Calculator ── */}
              <Route
                path="/damage-calculator"
                element={
                  <FeatureGatedRoute featureKey="feature_page_damage_calc">
                    <Layout showFooter={false} collapsibleHeader={true}>
                      <DamageCalculator />
                    </Layout>
                  </FeatureGatedRoute>
                }
              />

              {/* ── Pokédex ── */}
              <Route
                path="/pokedex"
                element={
                  <FeatureGatedRoute featureKey="feature_page_pokedex">
                    <Layout>
                      <Pokedex />
                    </Layout>
                  </FeatureGatedRoute>
                }
              />

              {/* ── Pokémon detail page ── */}
              <Route
                path="/pokedex/:id"
                element={
                  <FeatureGatedRoute featureKey="feature_page_pokedex">
                    <Layout>
                      <PokemonPage />
                    </Layout>
                  </FeatureGatedRoute>
                }
              />

              {/* ── Database hub + compendium pages ── */}
              <Route path="/database" element={<FeatureGatedRoute featureKey="feature_page_database"><Layout><DatabaseHub /></Layout></FeatureGatedRoute>} />
              <Route path="/moves" element={<FeatureGatedRoute featureKey="feature_page_moves"><Layout><Compendium kind="moves" /></Layout></FeatureGatedRoute>} />
              <Route path="/moves/:name" element={<FeatureGatedRoute featureKey="feature_page_moves"><Layout><MoveDetail /></Layout></FeatureGatedRoute>} />
              <Route path="/items" element={<FeatureGatedRoute featureKey="feature_page_items"><Layout><Compendium kind="items" /></Layout></FeatureGatedRoute>} />
              <Route path="/abilities" element={<FeatureGatedRoute featureKey="feature_page_abilities"><Layout><Compendium kind="abilities" /></Layout></FeatureGatedRoute>} />
              <Route path="/abilities/:name" element={<FeatureGatedRoute featureKey="feature_page_abilities"><Layout><AbilityDetail /></Layout></FeatureGatedRoute>} />

              {/* ── Counter ── */}
              <Route
                path="/counter"
                element={
                  <FeatureGatedRoute featureKey="feature_page_counter">
                    <Layout>
                      <CounterPage />
                    </Layout>
                  </FeatureGatedRoute>
                }
              />

              {/* ── Type Chart ── */}
              <Route
                path="/type-chart"
                element={
                  <FeatureGatedRoute featureKey="feature_page_type_chart">
                    <Layout>
                      <TypeChart />
                    </Layout>
                  </FeatureGatedRoute>
                }
              />

              {/* ── Sets ── */}
              <Route
                path="/sets"
                element={
                  <FeatureGatedRoute featureKey="feature_page_sets">
                    <Layout>
                      <SetsPage />
                    </Layout>
                  </FeatureGatedRoute>
                }
              />

              {/* ── Team Builder ── */}
              <Route
                path="/team-builder"
                element={
                  <FeatureGatedRoute featureKey="feature_page_team_builder">
                    <Layout showFooter={false} collapsibleHeader={true}>
                      <TeamBuilder />
                    </Layout>
                  </FeatureGatedRoute>
                }
              />

              {/* ── Dungeon Guides (login-gated) ── */}
              <Route
                path="/dungeons"
                element={<FeatureGatedRoute featureKey="feature_page_dungeons"><Layout><ProtectedRoute><DungeonsPage /></ProtectedRoute></Layout></FeatureGatedRoute>}
              />
              <Route
                path="/dungeons/:id"
                element={<FeatureGatedRoute featureKey="feature_page_dungeons"><Layout><ProtectedRoute><DungeonView /></ProtectedRoute></Layout></FeatureGatedRoute>}
              />

              {/* ── PvP Arena ── */}
              <Route
                path="/pvp"
                element={
                  <FeatureGatedRoute featureKey="feature_page_pvp">
                    <Layout>
                      <ProtectedRoute>
                        <PvpHub />
                      </ProtectedRoute>
                    </Layout>
                  </FeatureGatedRoute>
                }
              />
              <Route
                path="/pvp/battle/:battleId"
                element={
                  <FeatureGatedRoute featureKey="feature_page_pvp">
                    <Layout showFooter={false}>
                      <ProtectedRoute>
                        <BattleView />
                      </ProtectedRoute>
                    </Layout>
                  </FeatureGatedRoute>
                }
              />
              <Route
                path="/pvp/watch/:battleId"
                element={
                  <FeatureGatedRoute featureKey="feature_page_pvp">
                    <Layout showFooter={false}>
                      <ProtectedRoute>
                        <SpectatorView />
                      </ProtectedRoute>
                    </Layout>
                  </FeatureGatedRoute>
                }
              />
              <Route
                path="/pvp/replay/:battleId"
                element={
                  <FeatureGatedRoute featureKey="feature_page_pvp">
                    <Layout>
                      <ProtectedRoute>
                        <ReplayView source="authed" />
                      </ProtectedRoute>
                    </Layout>
                  </FeatureGatedRoute>
                }
              />
              {/* Public shared replay — no login required */}
              <Route
                path="/replay/:battleId"
                element={<Layout><ReplayView source="public" /></Layout>}
              />

              {/* ── Games ── */}
              <Route path="/games" element={<FeatureGatedRoute featureKey="feature_page_games"><Layout><GamesHub /></Layout></FeatureGatedRoute>} />
              <Route path="/dex-guesser" element={<FeatureGatedRoute featureKey="feature_page_games"><Layout><DexGuesser /></Layout></FeatureGatedRoute>} />
              <Route path="/spin-wheel" element={<FeatureGatedRoute featureKey="feature_page_games"><Layout><SpinWheel /></Layout></FeatureGatedRoute>} />
              <Route path="/random-team" element={<FeatureGatedRoute featureKey="feature_page_games"><Layout><RandomTeam /></Layout></FeatureGatedRoute>} />
              <Route path="/type-quiz" element={<FeatureGatedRoute featureKey="feature_page_games"><Layout><TypeQuiz /></Layout></FeatureGatedRoute>} />

              {/* ── Impressum ── */}
              <Route
                path="/impressum"
                element={
                  <Layout>
                    <Impressum />
                  </Layout>
                }
              />

              {/* ── Authentication Routes – no layout wrapper ── */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route
                path="/setup-totp"
                element={
                  <ProtectedRoute>
                    <TOTPSetup />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin-panel"
                element={<Navigate to="/admin/overview" replace />}
              />
              {/* ── Admin sub-pages ── */}
              <Route path="/admin/overview" element={<Layout><ProtectedRoute><AdminOverview /></ProtectedRoute></Layout>} />
              <Route path="/admin/users" element={<Layout><ProtectedRoute><AdminUsers /></ProtectedRoute></Layout>} />
              <Route path="/admin/logging" element={<Layout><ProtectedRoute><LoggingPanel /></ProtectedRoute></Layout>} />
              <Route path="/admin/feature-flags" element={<Layout><ProtectedRoute><FeatureFlagsAdmin /></ProtectedRoute></Layout>} />
              <Route path="/admin/pvp-stats" element={<Layout><ProtectedRoute><PvpStats /></ProtectedRoute></Layout>} />
              <Route path="/admin/atlas" element={<Layout><ProtectedRoute><AtlasAdmin /></ProtectedRoute></Layout>} />
              <Route path="/admin/learnsets" element={<Layout><ProtectedRoute><LearnsetGenerator /></ProtectedRoute></Layout>} />
              {/* ── Admin: Game Data Management (per-section pages) ── */}
              <Route path="/admin/game-data" element={<Navigate to="/admin/game-data/abilities" replace />} />
              <Route
                path="/admin/game-data/:section"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <GameDataManagement />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path="/profile"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  </Layout>
                }
              />

              {/* ── Discord Dashboard – standalone routes ── */}
              <Route
                path="/dashboard"
                element={
                  <FeatureGatedRoute featureKey="feature_page_discord_bot">
                    <DiscordDashboard />
                  </FeatureGatedRoute>
                }
              />
              <Route
                path="/dashboard/server/:guildId"
                element={
                  <FeatureGatedRoute featureKey="feature_page_discord_bot">
                    <DiscordServerSettings />
                  </FeatureGatedRoute>
                }
              />

              {/* ── Legacy redirects ── */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/admin/map-region-editor" element={<Navigate to="/map" replace />} />
            </Routes>
            </Suspense>
            {/* Not requesting the lazy chunk at all while AI is off — see
                config/env.ts — saves downloading its bundled dex/moves/items/
                abilities/coverage data just to render null. */}
            {AI_UI_ENABLED && <Suspense fallback={null}><ChatBubble /></Suspense>}
          </div>
        </Router>
        </GameDataProvider>
      </FeatureFlagsProvider>
    </CustomAuthProvider>
    </ToastProvider>
    </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
